import { addBusinessDays, format } from '@if/core/date'
import { EPaymentFrequency, EProvince } from '@if/core/types'
import { CalculatorEstimateRequestDto, EProtectionPlanCoverage } from '@src/types/CalculatorEstimateDto'
import { Constants, CreditRatingBands, CreditRatingOption, PersonalLoanCalculatorConstants } from '@src/types/Constants'

/** Ranges only — the credit engine resolves each band's rate from the ERS. */
export const buildCreditRatingOptions = (): CreditRatingOption[] =>
  CreditRatingBands.map((band) => ({
    minErs: band.minErs,
    maxErs: band.maxErs,
    nameKey: band.nameKey,
  }))

/**
 * `ersScore` is the band's lower bound, so a quote never flatters the bottom of a tier.
 * `firstPaymentOn` must be a date the program allows, or the quote prices differently from the
 * worksheet — pass the first option the API returned.
 */
export const createEstimateRequest = (
  merchantId: string,
  provinceIso: EProvince,
  amountRequested: number,
  paymentPlanId: string,
  term: number | null,
  ersScore: number,
  protectionPlan: EProtectionPlanCoverage,
  firstPaymentOn: string,
): CalculatorEstimateRequestDto => {
  const { deliveryOn } = getDeliveryAndFirstPaymentDates()

  return {
    merchantId,
    ersScore,
    provinceIso,
    amountRequested,
    paymentPlanId,
    paymentFrequency: EPaymentFrequency.Monthly,
    term,
    protectionPlan,
    deliveryOn,
    firstPaymentOn,
  }
}

export const createComputeMonthTermFrequency = (
  merchantId: string,
  provinceIso: EProvince,
  paymentPlanId: string,
  creditRatingInterestRate: number,
  amountRequested: number,
  includeInsurance: boolean,
  /** Joint coverage costs more, so it can change which terms stay under the legal APR. */
  isJointCoverage: boolean,
  /** Must be the same date the estimate priced on, or the terms answer for a different loan. */
  firstPaymentOn: string,
) => {
  const { deliveryOn } = getDeliveryAndFirstPaymentDates()

  return {
    merchantId,
    paymentPlanId,
    interestRate: creditRatingInterestRate,
    hasCoapplicant: isJointCoverage,
    stateIso: provinceIso,
    paymentFrequency: EPaymentFrequency.Monthly,
    deliveryOn,
    firstPaymentOn,
    maxPmtAmount: PersonalLoanCalculatorConstants.MAX_PAYMENT_AMOUNT,
    amountRequested,
    includeInsurance,
    include84Months: (amountRequested ?? 0) >= Constants.LargeLoanAmount,
  }
}

export const shouldAutoSelectTerm = (
  term: number | undefined,
  paymentPlanId: string | undefined,
  previousPaymentPlanId: string | null,
  isSelectedTermStillOffered: boolean,
): boolean => {
  const paymentPlanChanged = paymentPlanId && paymentPlanId !== previousPaymentPlanId
  return !term || !!paymentPlanChanged || !isSelectedTermStillOffered
}

export const getDeliveryAndFirstPaymentDates = () => {
  const deliveryOn = format(new Date(), 'yyyy-MM-dd')
  const firstPaymentOn = format(
    addBusinessDays(deliveryOn, PersonalLoanCalculatorConstants.BUSINESS_DAYS_OFFSET),
    'yyyy-MM-dd',
  )
  return { deliveryOn, firstPaymentOn }
}
